import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface RowActionProps {
  onClick: () => void;
  children: ReactNode;
  icon?: ReactNode;
  /** Destructive actions read muted at rest and only turn red on hover — see below. */
  tone?: "default" | "danger";
  disabled?: boolean;
  className?: string;
}

/**
 * A table row's action control.
 *
 * These were bare text links with no hit area beyond the glyphs themselves — a ~14px tall target
 * that is awkward with a mouse and genuinely hard on touch. This gives them a real 32px box,
 * a hover surface, and a visible focus ring.
 *
 * Destructive actions rest in muted text and only take danger colour on hover/focus. A table with
 * eight rows of resting red reads as eight errors, and it spends the one colour that should mean
 * "this is irreversible" — which is saved for the confirmation dialog's button.
 */
export function RowAction({
  onClick,
  children,
  icon,
  tone = "default",
  disabled = false,
  className,
}: RowActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-control px-2.5 text-xs font-medium",
        "transition-colors duration-fast ease-standard",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        "disabled:cursor-not-allowed disabled:opacity-40",
        tone === "danger"
          ? "text-text-muted hover:bg-danger-soft hover:text-[var(--sb-danger-on-soft)] focus-visible:bg-danger-soft focus-visible:text-[var(--sb-danger-on-soft)]"
          : "text-text-muted hover:bg-surface-hover hover:text-text",
        className,
      )}
    >
      {icon && <span className="inline-flex shrink-0">{icon}</span>}
      {children}
    </button>
  );
}

/** Groups row actions with consistent spacing and right alignment. */
export function RowActions({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-0.5">{children}</div>;
}
